/**
 * The job queue as data freshness: the latest `jobs` row per kind for a brand, a plain label and
 * schedule for each kind (spec §7 times, Task 2 SCHEDULE), and what its state means. A job that
 * is `skipped` with `result.reason = 'missing_secret'` is waiting for a key: an info state, not an
 * error. Manual runs go through `POST /run {brand_id, kind}` (editor).
 */
import { callApi } from '../api';
import { queryClient, useSb } from '../query';
import { supabase } from '../supabase';
import { humanize } from '../format';
import type { Tables } from '../database.types';
import type { StatusTone } from '../../components/StatusPill';

export type JobRow = Pick<Tables<'jobs'>, 'id' | 'brand_id' | 'kind' | 'status' | 'created_at' | 'finished_at' | 'result' | 'error' | 'attempts'>;

export type KindMeta = { label: string; schedule: string; maxAgeHours: number };

const DAILY = 26;
const WEEKLY = 24 * 7 + 6;
const MONTHLY = 24 * 32;

/** In the order the day runs. Kinds not listed still show, with a humanized name. */
export const JOB_KINDS: Record<string, KindMeta> = {
  ai_dispatch: { label: 'AI checks sent', schedule: 'Daily 05:00 UTC', maxAgeHours: DAILY },
  ai_collect: { label: 'AI answers', schedule: 'Daily, after 05:00 UTC', maxAgeHours: DAILY },
  ai_analyze: { label: 'Answer analysis', schedule: 'After AI answers', maxAgeHours: DAILY },
  ai_scores: { label: 'AI scores', schedule: 'After answer analysis', maxAgeHours: DAILY },
  source_check: { label: 'AI sources', schedule: 'After answer analysis', maxAgeHours: WEEKLY },
  rank_dispatch: { label: 'Rank checks sent', schedule: 'Daily 06:00 UTC', maxAgeHours: DAILY },
  rank_collect: { label: 'Rank results', schedule: 'Daily, after 06:00 UTC', maxAgeHours: DAILY },
  maps_dispatch: { label: 'Maps checks sent', schedule: 'Weekly 06:00 UTC', maxAgeHours: WEEKLY },
  maps_collect: { label: 'Maps results', schedule: 'Weekly, after 06:00 UTC', maxAgeHours: WEEKLY },
  weekly_refresh: { label: 'Weekly refresh', schedule: 'Mondays 07:00 UTC', maxAgeHours: WEEKLY },
  bot_check: { label: 'AI bot access', schedule: 'Mondays 07:00 UTC', maxAgeHours: WEEKLY },
  mentions_refresh: { label: 'LLM mentions', schedule: 'Weekly, after the refresh', maxAgeHours: WEEKLY },
  listings_refresh: { label: 'Local listings', schedule: 'Weekly, after the refresh', maxAgeHours: WEEKLY },
  audit_start: { label: 'Site audit started', schedule: 'Day 1 of the month, 08:00 UTC', maxAgeHours: MONTHLY },
  audit_collect: { label: 'Site audit', schedule: 'Monthly, after the crawl', maxAgeHours: MONTHLY },
  gsc_sync: { label: 'Search Console', schedule: 'Daily 09:00 UTC', maxAgeHours: DAILY },
  analytics_sync: { label: 'AI traffic', schedule: 'Daily 09:00 UTC', maxAgeHours: DAILY },
  engine: { label: 'Opportunity engine', schedule: 'Daily 10:00 UTC', maxAgeHours: DAILY },
  content_plan: { label: 'Content plan', schedule: 'Daily 11:00 UTC', maxAgeHours: DAILY },
  web_mentions: { label: 'Web mentions', schedule: 'Mondays 08:00 UTC', maxAgeHours: WEEKLY },
  geogrid_dispatch: { label: 'Geo-grid scans sent', schedule: 'Weekly 06:00 UTC', maxAgeHours: WEEKLY },
  geogrid_collect: { label: 'Geo-grid results', schedule: 'Weekly, after 06:00 UTC', maxAgeHours: WEEKLY },
  digest: { label: 'Weekly report', schedule: 'Mondays 11:00 UTC', maxAgeHours: WEEKLY },
  notify: { label: 'Notifications', schedule: 'After the engine and reports', maxAgeHours: WEEKLY },
};

const ORDER = Object.keys(JOB_KINDS);

export function kindMeta(kind: string): KindMeta {
  return JOB_KINDS[kind] ?? { label: humanize(kind), schedule: '–', maxAgeHours: WEEKLY };
}

/**
 * Kinds an editor may start from the dashboard. They cost nothing (the engine is plain SQL and
 * TypeScript; Google and robots.txt fetches are free). Paid collectors run on schedule, or from
 * their own screens where the cost is shown first.
 */
export const RUN_FROM_DASHBOARD = new Set(['engine', 'gsc_sync', 'analytics_sync', 'bot_check']);

/** The newest row per kind, in the order the day runs. */
export function latestPerKind(rows: JobRow[]): JobRow[] {
  const sorted = [...rows].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  const seen = new Map<string, JobRow>();
  for (const r of sorted) if (!seen.has(r.kind)) seen.set(r.kind, r);
  const rank = (k: string) => {
    const i = ORDER.indexOf(k);
    return i < 0 ? ORDER.length : i;
  };
  return [...seen.values()].sort((a, b) => rank(a.kind) - rank(b.kind) || a.kind.localeCompare(b.kind));
}

export function useLatestJobs(brandId: string | null) {
  return useSb(
    ['seo', 'jobs', 'latest-per-kind', brandId],
    async () => {
      const r = await supabase
        .from('jobs')
        .select('id, brand_id, kind, status, created_at, finished_at, result, error, attempts')
        .eq('brand_id', brandId!)
        .order('created_at', { ascending: false })
        .limit(400);
      return { data: latestPerKind((r.data ?? []).filter((j) => j.brand_id === brandId)), error: r.error };
    },
    { enabled: !!brandId },
  );
}

export type JobState = { tone: StatusTone; label: string; note: string | null; waitingFor: string | null };

/** What a job's status means for the person reading the freshness table. */
export function jobState(job: Pick<JobRow, 'status' | 'result' | 'error'>): JobState {
  const result = (job.result && typeof job.result === 'object' && !Array.isArray(job.result) ? job.result : {}) as Record<string, unknown>;
  const reason = typeof result.reason === 'string' ? result.reason : null;
  switch (job.status) {
    case 'done':
      return { tone: 'ok', label: 'Done', note: null, waitingFor: null };
    case 'failed':
      return { tone: 'bad', label: 'Failed', note: job.error ? job.error.slice(0, 240) : 'It failed after its retries.', waitingFor: null };
    case 'queued':
      return { tone: 'info', label: 'Queued', note: null, waitingFor: null };
    case 'running':
      return { tone: 'info', label: 'Running', note: null, waitingFor: null };
    case 'skipped': {
      if (reason === 'missing_secret') {
        const name = typeof result.name === 'string' && result.name ? result.name : 'an API key';
        return { tone: 'info', label: `Waiting for ${name}`, note: 'It runs on its next schedule once the key is set.', waitingFor: name };
      }
      if (reason === 'budget_exhausted') return { tone: 'warn', label: 'Budget used up', note: 'It runs again next month, or when an admin raises the budget.', waitingFor: null };
      if (reason === 'paused') return { tone: 'off', label: 'Paused', note: 'Scheduled checks are paused in Settings.', waitingFor: null };
      if (reason === 'not_built') return { tone: 'off', label: 'Not built yet', note: null, waitingFor: null };
      return { tone: 'off', label: 'Skipped', note: reason ? humanize(reason) : null, waitingFor: null };
    }
    default:
      return { tone: 'off', label: humanize(job.status), note: null, waitingFor: null };
  }
}

/** POST /run. Returns the new job id. */
export async function runJob(brandId: string, kind: string, payload?: Record<string, unknown>): Promise<string> {
  const body = payload ? { brand_id: brandId, kind, payload } : { brand_id: brandId, kind };
  const res = await callApi<{ job_id: string }>('/run', body);
  void queryClient.invalidateQueries({ queryKey: ['seo', 'jobs'] });
  return res.job_id;
}
